/**
 * Editable recipient roster for a share link - lets staff add or
 * remove gate emails on an EXISTING link (holiday cover, new
 * procurement contact) without re-running the share wizard.
 *
 * Merge-safe by construction: it always loads the CURRENT list first
 * and POSTs the full updated list (the backend endpoint has replace
 * semantics), so adding one colleague never wipes the original
 * recipients.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, X, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@([A-Za-z0-9][A-Za-z0-9\-]*\.)+[A-Za-z]{2,}$/;
const PERSONAL = new Set([
  "gmail.com", "googlemail.com", "yahoo.com", "yahoo.co.uk", "yahoo.fr",
  "hotmail.com", "hotmail.co.uk", "outlook.com", "live.com", "msn.com",
  "icloud.com", "me.com", "mac.com", "aol.com", "proton.me", "protonmail.com",
  "gmx.com", "gmx.de", "mail.com", "yandex.com", "zoho.com", "fastmail.com",
]);

export default function RecipientsEditor({ token }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    setState({ loading: true, data: null, error: null });
    axios
      .get(`${API}/api/proposals/share/${encodeURIComponent(token)}/recipients`)
      .then((r) => setState({ loading: false, data: r.data, error: null }))
      .catch((e) => setState({
        loading: false, data: null,
        error: e?.response?.data?.detail || "Couldn't load recipients.",
      }));
  }, [token]);

  const recipients = state.data?.recipient_emails || [];
  const gateMode = state.data?.gate_mode;
  const isPending = gateMode === "pending_config";

  const save = async (next, successMsg) => {
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/api/proposals/share/${encodeURIComponent(token)}/recipients`,
        { recipient_emails: next },
      );
      setState((prev) => ({
        loading: false,
        data: { ...(prev.data || {}), ...data },
        error: null,
      }));
      toast.success(successMsg);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const addOne = async (raw) => {
    const email = (raw || "").trim().toLowerCase();
    if (!email) return;
    if (!EMAIL_RE.test(email)) {
      toast.error(`"${raw}" isn't a valid email.`);
      return;
    }
    const domain = email.split("@")[1];
    if (PERSONAL.has(domain)) {
      toast.error(`Personal email (${domain}) not allowed.`);
      return;
    }
    if (recipients.includes(email)) {
      toast.info("Already in the list.");
      return;
    }
    const ok = await save([...recipients, email], `Added ${email}`);
    if (ok) setDraft("");
  };

  const removeOne = (email) =>
    save(recipients.filter((e) => e !== email), `Removed ${email}`);

  if (!token) return null;

  return (
    <section
      data-testid="trail-recipients-editor"
      className={`rounded-xl border p-4 ${
        isPending ? "border-amber-200 bg-amber-50/50" : "border-stone-200 bg-white"
      }`}
    >
      <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-stone-400 mb-2 flex items-center gap-1.5">
        <Lock className={`w-3 h-3 ${isPending ? "text-amber-700" : "text-[#1B4F8A]"}`} />
        Who can open this link
        {gateMode && (
          <span
            className={`ml-1 text-[9px] font-medium normal-case tracking-normal ${
              isPending
                ? "text-amber-700"
                : gateMode === "gated"
                ? "text-emerald-700"
                : "text-stone-400"
            }`}
          >
            · {gateMode.replace("_", " ")}
          </span>
        )}
      </p>

      {state.loading && (
        <p className="text-[11px] font-mono text-stone-400">Loading recipients…</p>
      )}
      {state.error && (
        <p className="text-xs text-stone-500">Recipients unavailable: {state.error}</p>
      )}

      {!state.loading && !state.error && (
        <>
          <div className="flex flex-wrap items-center gap-1.5 min-h-[36px] px-2 py-1 border border-stone-200 rounded-md bg-white">
            {recipients.map((e) => (
              <span
                key={e}
                data-testid={`trail-recipient-chip-${e}`}
                className="inline-flex items-center gap-1 bg-[#1B4F8A]/8 text-[#1B4F8A] text-[11px] font-mono px-1.5 py-0.5 rounded border border-[#1B4F8A]/20"
              >
                {e}
                <button
                  type="button"
                  onClick={() => removeOne(e)}
                  disabled={saving}
                  className="hover:text-rose-600 disabled:opacity-50"
                  aria-label={`Remove ${e}`}
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            ))}
            <input
              type="email"
              value={draft}
              onChange={(ev) => setDraft(ev.target.value)}
              onKeyDown={(ev) => {
                if ((ev.key === "Enter" || ev.key === "," || ev.key === " ") && draft.trim()) {
                  ev.preventDefault();
                  addOne(draft);
                }
              }}
              onBlur={() => { if (draft.trim()) addOne(draft); }}
              placeholder="add@company.com"
              data-testid="trail-recipient-input"
              className="flex-1 min-w-[150px] bg-transparent text-xs text-slate-900 placeholder:text-stone-400 focus:outline-none py-0.5"
              disabled={saving}
            />
          </div>
          <p className="text-[10px] text-stone-400 mt-1.5">
            Adding an email takes effect immediately - the same link works,
            no re-send needed. Personal domains (Gmail etc.) are blocked.
          </p>
          {isPending && (
            <p className="text-[10px] font-mono text-amber-700 flex items-center gap-1 mt-1">
              <AlertTriangle className="w-3 h-3" /> Add at least one work email to open access.
            </p>
          )}
        </>
      )}
    </section>
  );
}
